import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Download,
  X,
  RefreshCw,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  DownloadCloud
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  BackgroundExportStatus,
  getAllExportsForConnection,
  downloadExportFile,
  cancelBackgroundExport,
  DownloadProgress,
  manualPrepareStreaming
} from '@/utils/organization/backgroundExport';
import { getDisplayExportStatus, isExportActive, isExportFinalizing, isExportReady } from '@/utils/organization/exportStatusDisplay';
import { useToast } from '@/hooks/use-toast';

interface GlobalExportManagerProps {
  connectionId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const GlobalExportManager: React.FC<GlobalExportManagerProps> = ({
  connectionId,
  isOpen,
  onClose
}) => {
  const [exports, setExports] = useState<BackgroundExportStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const channelRef = useRef<any>(null);

  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllExportsForConnection(connectionId);
      if (result.success && result.exports) {
        setExports(result.exports);
      }
    } catch (error) {
      console.error('Failed to fetch exports:', error);
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  // Single real-time connection for all exports in this connection
  useEffect(() => {
    if (!isOpen || !connectionId) return;

    // Initial fetch
    fetchExports();

    // Setup single realtime connection for all exports
    const channel = supabase
      .channel(`exports-${connectionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'csv_exports',
          filter: `connection_id=eq.${connectionId}`
        },
        (payload) => {

          if (payload.new) {
            const updatedExport = payload.new as BackgroundExportStatus;

            setExports(currentExports => {
              const existingIndex = currentExports.findIndex(
                exp => exp.export_id === updatedExport.export_id
              );

              if (existingIndex >= 0) {
                // Update existing export
                const newExports = [...currentExports];
                newExports[existingIndex] = updatedExport;
                return newExports;
              } else {
                // Add new export
                return [...currentExports, updatedExport];
              }
            });
          } else if (payload.old && payload.eventType === 'DELETE') {
            const deletedExport = payload.old as BackgroundExportStatus;
            setExports(currentExports =>
              currentExports.filter(exp => exp.export_id !== deletedExport.export_id)
            );
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
        } else if (status === 'CHANNEL_ERROR') {
          console.warn('Realtime connection failed, will use polling fallback');
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isOpen, connectionId, fetchExports]);

  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const handleDownloadAll = async () => {
    const completedExports = exports.filter((exp) => isExportReady(exp));

    if (completedExports.length === 0) {
      toast({
        title: "No Downloads Available",
        description: "No completed exports available for download.",
        variant: "warning"
      });
      return;
    }

    setIsDownloadingAll(true);

    try {
      for (const exportItem of completedExports) {
        try {
          // Get organization name using organisation_id
          const { data: orgData } = await supabase
            .from('organisations')
            .select('name')
            .eq('id', exportItem.organisation_id)
            .maybeSingle();

          const orgName = orgData?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'export';
          const filename = `${orgName}_${exportItem.table_name}.csv`;
          await downloadExportFile(exportItem.download_url!, filename, undefined, exportItem.export_id);
        } catch (error) {
          console.error(`Failed to download ${exportItem.export_id}:`, error);
        }
      }

      toast({
        title: "Downloads Complete",
        description: `Downloaded ${completedExports.length} completed exports.`,
      });
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleCancelAll = async () => {
    const activeExports = exports.filter((exp) => isExportActive(exp));

    if (activeExports.length === 0) {
      toast({
        title: "No Active Exports",
        description: "No active exports to cancel.",
        variant: "warning"
      });
      return;
    }

    let cancelledCount = 0;
    for (const exportItem of activeExports) {
      try {
        const result = await cancelBackgroundExport(exportItem.export_id);
        if (result.success) {
          cancelledCount++;
        }
      } catch (error) {
        console.error(`Failed to cancel ${exportItem.export_id}:`, error);
      }
    }

    toast({
      title: "Exports Cancelled",
      description: `Successfully cancelled ${cancelledCount} of ${activeExports.length} active exports.`,
    });

    // Refresh the list
    fetchExports();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'finalizing': return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'failed': return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'processing': return <Clock className="h-4 w-4 text-blue-600 animate-pulse" />;
      case 'combining': return <RefreshCw className="h-4 w-4 text-purple-600 animate-spin" />;
      case 'queued': return <Clock className="h-4 w-4 text-yellow-600" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'finalizing': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'combining': return 'bg-purple-100 text-purple-800';
      case 'queued': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <Card className="fixed bottom-4 right-4 w-96 max-h-96 shadow-lg z-50" data-testid="global-export-manager">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Export Manager ({exports.length})
          </span>
          <Button onClick={onClose} variant="ghost" size="sm" data-testid="close-export-manager">
            <X className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex gap-2 mb-3">
          <Button
            onClick={handleDownloadAll}
            size="sm"
            variant="default"
            disabled={!exports.some((exp) => isExportReady(exp)) || isDownloadingAll}
          >
            <DownloadCloud className={`h-3 w-3 mr-1 ${isDownloadingAll ? 'animate-bounce' : ''}`} />
            {isDownloadingAll ? 'Downloading...' : 'Download All'}
          </Button>
          <Button
            onClick={handleCancelAll}
            size="sm"
            variant="outline"
            disabled={!exports.some((exp) => isExportActive(exp))}
          >
            <X className="h-3 w-3 mr-1" />
            Cancel Active
          </Button>
          <Button onClick={fetchExports} size="sm" variant="ghost">
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>

        <Separator className="mb-3" />

        <ScrollArea className="h-48">
          {loading ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              Loading exports...
            </div>
          ) : exports.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              No exports found
            </div>
          ) : (
            <div className="space-y-2">
              {exports.map((exportItem) => (
                <ExportItem
                  key={exportItem.export_id}
                  exportStatus={exportItem}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

interface ExportItemProps {
  exportStatus: BackgroundExportStatus;
}

const ExportItem: React.FC<ExportItemProps> = memo(({ exportStatus }) => {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!isExportReady(exportStatus) || !exportStatus.download_url || isDownloading) return;

    setIsDownloading(true);

    try {
      // Get organization name using organisation_id
      const { data: orgData } = await supabase
        .from('organisations')
        .select('name')
        .eq('id', exportStatus.organisation_id)
        .maybeSingle();

      const orgName = orgData?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'export';
      const filename = `${orgName}_${exportStatus.table_name}.csv`;
      await downloadExportFile(exportStatus.download_url, filename, undefined, exportStatus.export_id);
      toast({
        title: "Download Complete",
        description: "Your CSV file has been downloaded successfully.",
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: error instanceof Error ? error.message : "Failed to download file",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  }, [exportStatus.download_url, exportStatus.schema_name, exportStatus.table_name, exportStatus.export_id, toast, isDownloading]);

  const handleFinalize = useCallback(async () => {
    if (isFinalizing) return;

    setIsFinalizing(true);

    try {
      const result = await manualPrepareStreaming(exportStatus.export_id, exportStatus.chunk_count || 0);
      if (result.success) {
        toast({
          title: "Export Finalized",
          description: "The export has been finalized and is ready for download.",
        });
      } else {
        toast({
          title: "Finalization Failed",
          description: result.error || "Failed to finalize export",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Finalization Failed",
        description: error instanceof Error ? error.message : "Failed to finalize export",
        variant: "destructive"
      });
    } finally {
      setIsFinalizing(false);
    }
  }, [exportStatus.export_id, exportStatus.chunk_count, isFinalizing, toast]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-3 w-3 text-green-600" />;
      case 'finalizing': return <RefreshCw className="h-3 w-3 text-blue-600 animate-spin" />;
      case 'failed': return <AlertCircle className="h-3 w-3 text-red-600" />;
      case 'processing': return <Clock className="h-3 w-3 text-blue-600 animate-pulse" />;
      case 'combining': return <RefreshCw className="h-3 w-3 text-purple-600 animate-spin" />;
      case 'queued': return <Clock className="h-3 w-3 text-yellow-600" />;
      default: return <FileText className="h-3 w-3" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'finalizing': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'combining': return 'bg-purple-100 text-purple-800';
      case 'queued': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const displayStatus = getDisplayExportStatus(exportStatus);
  const isReady = isExportReady(exportStatus);
  const isFinalizingStatus = isExportFinalizing(exportStatus);

  return (
    <div className="flex items-center justify-between p-2 border rounded text-xs">
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {getStatusIcon(displayStatus)}
          <span className="truncate font-medium">{exportStatus.table_name}</span>
          <Badge className={`${getStatusColor(displayStatus)} text-xs px-1 py-0`}>
            {displayStatus}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground ml-5">
          {formatDate(exportStatus.created_at)}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {isReady && (
          <Button onClick={handleDownload} size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={isDownloading}>
            <Download className={`h-3 w-3 ${isDownloading ? 'animate-bounce' : ''}`} />
          </Button>
        )}

        {/* Finalize button for stuck combining or completed-chunk exports */}
        {(exportStatus.status === 'combining' || isFinalizingStatus ||
          (exportStatus.chunks_completed && exportStatus.chunk_count &&
            exportStatus.chunks_completed >= exportStatus.chunk_count &&
            exportStatus.status === 'processing')) && (
            <Button
              onClick={handleFinalize}
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              disabled={isFinalizing}
              title="Finalize export now"
            >
              <RefreshCw className={`h-3 w-3 ${isFinalizing ? 'animate-spin' : ''}`} />
            </Button>
          )}
      </div>
    </div>
  );
});

export default GlobalExportManager;